import { useEffect, useState } from 'react'
import { useImmer } from 'use-immer'
import s from '../Opportunity.scss'
import { Checkbox, InputContainer, KeyValuePairs } from 'components/ideal-core-ui'
import { Button, Container, Row, Col } from 'reactstrap'
import capitalize from 'lodash/capitalize'
import Select from 'react-select'
import {
  OPTIONS,
  YEAR_FORMAT_OPTIONS,
  MONTH_FORMAT_OPTIONS,
  RESET_EVERY_OPTIONS
} from './constants'
import { useLoading } from 'hooks'
import { makePutRequest, makeGetRequest } from 'utils/api'
import { toDealStatuses, toStatusesOptions } from './serializers'

const QuoteFormats = ({ saveDetails, details }) => {
  const [quoteFormats, setQuoteFormats] = useImmer(null)
  const [projectFormats, setProjectFormats] = useImmer(null)
  const [, withLoadingLocks] = useLoading()
  const [dealStatuses, setDealStatuses] = useState([])
  const [statuses, setStatuses] = useState([])

  const resetCurrentNumber = async (id, cb) => {
    const { nextNumber } = await withLoadingLocks(
      makePutRequest(`/v1/organization/reset-number-automation/${id}`)
    )
    cb?.(nextNumber)
  }

  useEffect(() => {
    const quote = details.numberAutomations.find(n => n.automationType === 'QUOTE_NUMBER')
    const project = details.numberAutomations.find(n => n.automationType === 'PROJECT_NUMBER')
    setQuoteFormats(quote)
    setProjectFormats(project)
    setStatuses(details.dealSettings.projectNumberDealStatuses)
  }, [details, setProjectFormats, setQuoteFormats])

  useEffect(() => {
    ;(async () => {
      const response = await makeGetRequest('/v1/dealstatus')
      setDealStatuses(toDealStatuses(response))
    })()
  }, [])

  return ['quote', 'project'].map(type => {
    let formats, setFormats
    if (type === 'quote') {
      formats = quoteFormats
      setFormats = setQuoteFormats
    } else {
      formats = projectFormats
      setFormats = setProjectFormats
    }

    return (
      <div key={type}>
        <div className={s.settingsItem}>
          <h5 style={{ marginRight: '15px', display: 'inline-block' }}>
            Set {capitalize(type)} Number Format and Increment
          </h5>
          <Checkbox
            size="md"
            isChecked={formats?.automationValue}
            triggerCheckbox={value => {
              setFormats(draft => {
                draft.automationValue = value.target.checked
              })
            }}
          />
          <p>
            When this options is selected, the system will automatically calculate the next
            opportunity {type} number based on the rules set below. You can take this next number
            and assign it to your opportunity. The quote number will still remaineditable on each
            opportunity, it is just pre-populated for your convenience. <br />
            <br />
            For example, let&apos;s consider your quote number that starts from 0 every month and is
            in this format 22-P04-25. You will set current year format to 22, Text1 to P, Current
            Month to 04 format, and Reset Increment Every month
          </p>
          <h6
            style={{
              marginTop: 30,
              marginBottom: 30,
              display: 'inline-block',
              fontWeight: 'bold'
            }}
          >
            Set {type} format. Select which field goes in which position and set a 1-character
            separator if need. You can leave the separator blank, and you don’t have to include all
            6 fields. Just set the ones you need per example above
          </h6>
          <div className={s.numberFormatWrapper}>
            <Select
              value={formats?.firstOptionType}
              className={s.selectFormats}
              placeholder="Select or leave Blank"
              onChange={value =>
                setFormats(draft => {
                  draft.firstOptionType = value
                })
              }
              options={OPTIONS}
              isClearable
            />
            <InputContainer
              allowWhitespace
              style={{ float: 'left', width: 'auto', display: 'block', margin: '0 10px 0 10px' }}
              type="text"
              cb={value => {
                setFormats(draft => {
                  draft.firstSeparator = value
                })
              }}
              maxLength="1"
              text={formats?.firstSeparator ?? ''}
              classname={s.inputFormats}
            />
            <Select
              value={formats?.secondOptionType}
              className={s.selectFormats}
              placeholder="Select or leave Blank"
              onChange={value =>
                setFormats(draft => {
                  draft.secondOptionType = value
                })
              }
              options={OPTIONS}
              isClearable
            />
            <InputContainer
              allowWhitespace
              style={{ float: 'left', width: 'auto', display: 'block', margin: '0 10px 0 10px' }}
              type="text"
              cb={value => {
                setFormats(draft => {
                  draft.secondSeparator = value
                })
              }}
              maxLength="1"
              text={formats?.secondSeparator ?? ''}
              classname={s.inputFormats}
            />
            <Select
              value={formats?.thirdOptionType}
              className={s.selectFormats}
              placeholder="Select or leave Blank"
              onChange={value =>
                setFormats(draft => {
                  draft.thirdOptionType = value
                })
              }
              options={OPTIONS}
              isClearable
            />
            <InputContainer
              allowWhitespace
              style={{ float: 'left', width: 'auto', display: 'block', margin: '0 10px 0 10px' }}
              type="text"
              cb={value => {
                setFormats(draft => {
                  draft.thirdSeparator = value
                })
              }}
              maxLength="1"
              text={formats?.thirdSeparator ?? ''}
              classname={s.inputFormats}
            />
            <Select
              value={formats?.fourthOptionType}
              className={s.selectFormats}
              placeholder="Select or leave Blank"
              onChange={value =>
                setFormats(draft => {
                  draft.fourthOptionType = value
                })
              }
              options={OPTIONS}
              isClearable
            />
            <InputContainer
              allowWhitespace
              style={{ float: 'left', width: 'auto', display: 'block', margin: '0 10px 0 10px' }}
              type="text"
              cb={value => {
                setFormats(draft => {
                  draft.fourthSeparator = value
                })
              }}
              maxLength="1"
              text={formats?.fourthSeparator ?? ''}
              classname={s.inputFormats}
            />
            <Select
              value={formats?.fifthOptionType}
              className={s.selectFormats}
              placeholder="Select or leave Blank"
              onChange={value =>
                setFormats(draft => {
                  draft.fifthOptionType = value
                })
              }
              options={OPTIONS}
              isClearable
            />
            <InputContainer
              allowWhitespace
              style={{ float: 'left', width: 'auto', display: 'block', margin: '0 10px 0 10px' }}
              type="text"
              cb={value => {
                setFormats(draft => {
                  draft.fifthSeparator = value
                })
              }}
              maxLength="1"
              text={formats?.fifthSeparator ?? ''}
              classname={s.inputFormats}
            />
            <Select
              value={formats?.sixthOptionType}
              className={s.selectFormats}
              placeholder="Select or leave Blank"
              onChange={value =>
                setFormats(draft => {
                  draft.sixthOptionType = value
                })
              }
              options={OPTIONS}
              isClearable
            />
          </div>
          <div className={s.clearfix} />
          <h6
            style={{
              marginTop: 30,
              marginBottom: 30,
              display: 'inline-block',
              fontWeight: 'bold'
            }}
          >
            Set the fields of the {type} number
          </h6>
          <Container>
            <Row>
              <Col lg="6">
                <KeyValuePairs
                  pairs={[
                    {
                      key: 'Set Current Year Format',
                      keyClass: [s.label],
                      valueClass: [s.value],
                      value: (
                        <Select
                          value={formats?.yearFormat}
                          className={s.selectFormats}
                          placeholder="Select or leave Blank"
                          onChange={value =>
                            setFormats(draft => {
                              draft.yearFormat = value
                            })
                          }
                          options={YEAR_FORMAT_OPTIONS}
                          isClearable
                        />
                      )
                    },
                    {
                      key: 'Set Current Month Format',
                      keyClass: [s.label],
                      valueClass: [s.value],
                      value: (
                        <Select
                          value={formats?.monthFormat}
                          className={s.selectFormats}
                          placeholder="Select or leave Blank"
                          onChange={value =>
                            setFormats(draft => {
                              draft.monthFormat = value
                            })
                          }
                          options={MONTH_FORMAT_OPTIONS}
                          isClearable
                        />
                      )
                    },
                    {
                      key: 'Current Used Number',
                      keyClass: [s.label],
                      valueClass: [s.value],
                      value: (
                        <>
                          <InputContainer
                            isDisabled
                            type="number"
                            text={formats?.currentNumber ?? ''}
                          />
                          <Button
                            style={{ marginTop: 10 }}
                            onClick={() => {
                              resetCurrentNumber(formats.id, nextNumber => {
                                if (type === 'quote') {
                                  setQuoteFormats(draft => {
                                    draft.currentNumber = nextNumber
                                  })
                                } else {
                                  setProjectFormats(draft => {
                                    draft.currentNumber = nextNumber
                                  })
                                }
                              })
                            }}
                          >
                            Reset to Start From Value
                          </Button>
                        </>
                      )
                    },
                    {
                      key: 'Number Increment to Start From',
                      keyClass: [s.label],
                      valueClass: [s.value],
                      value: (
                        <>
                          <InputContainer
                            type="number"
                            placeholder="Enter number to start from"
                            cb={value =>
                              setFormats(draft => {
                                draft.startNumber = value
                              })
                            }
                            text={formats?.startNumber ?? ''}
                          />
                        </>
                      )
                    },
                    {
                      key: 'Reset Increment Every',
                      keyClass: [s.label],
                      valueClass: [s.value],
                      value: (
                        <Select
                          value={formats?.resetType}
                          className={s.selectFormats}
                          placeholder="Select or leave Blank"
                          onChange={value =>
                            setFormats(draft => {
                              draft.resetType = value
                            })
                          }
                          options={RESET_EVERY_OPTIONS}
                          isClearable
                        />
                      )
                    }
                  ]}
                />
              </Col>
              <Col lg="6">
                <KeyValuePairs
                  pairs={[
                    {
                      key: 'Text Field 1 (fixed text)',
                      keyClass: [s.label],
                      valueClass: [s.value],
                      value: (
                        <InputContainer
                          type="text"
                          placeholder="Enter text"
                          cb={value =>
                            setFormats(draft => {
                              draft.firstText = value
                            })
                          }
                          text={formats?.firstText ?? ''}
                        />
                      )
                    },
                    {
                      key: 'Text Field 2 (fixed text)',
                      keyClass: [s.label],
                      valueClass: [s.value],
                      value: (
                        <InputContainer
                          type="text"
                          placeholder="Enter text"
                          cb={value =>
                            setFormats(draft => {
                              draft.secondText = value
                            })
                          }
                          text={formats?.secondText ?? ''}
                        />
                      )
                    },
                    {
                      key: 'Text Field 3 (fixed text)',
                      keyClass: [s.label],
                      valueClass: [s.value],
                      value: (
                        <InputContainer
                          type="text"
                          placeholder="Enter text"
                          cb={value =>
                            setFormats(draft => {
                              draft.thirdText = value
                            })
                          }
                          text={formats?.thirdText ?? ''}
                        />
                      )
                    }
                  ]}
                />
              </Col>
            </Row>
          </Container>
        </div>
        {type === 'project' && (
          <>
            <p className={s.description}>Select Opportunity Status</p>
            <Select
              isMulti
              onChange={values => setStatuses(values.map(v => v.value))}
              options={dealStatuses}
              className={s.select}
              value={toStatusesOptions(statuses, dealStatuses)}
            />
          </>
        )}
        <Button
          className={s.saveBtn}
          onClick={() =>
            saveDetails({
              ...details,
              numberAutomations: [
                type === 'quote'
                  ? quoteFormats
                  : details.numberAutomations.find(n => n.automationType === 'QUOTE_NUMBER'),
                type === 'project'
                  ? projectFormats
                  : details.numberAutomations.find(n => n.automationType === 'PROJECT_NUMBER')
              ],
              dealSettings: {
                ...details.dealSettings,
                projectNumberDealStatuses: statuses
              }
            })
          }
        >
          Save {capitalize(type)} Settings
        </Button>
      </div>
    )
  })
}

export default QuoteFormats
